import React, { useState, useMemo, useEffect } from 'react';
import { Table, Card, Checkbox, Radio, Row, Col, Form, Input, Button, Select, message } from 'antd';
import './index.scss';
import dayjs from 'dayjs';
import { updateRisk } from '../../../service/report';
import { ColumnsType } from 'antd/es/table';
import { RecordType, RiskListItemType } from '../types';

interface IActiveCardDetailProps {
    activeCardInfo: RecordType
    flowId: string
    onEvent: (eventName: string, params?: any) => void
    isReportAgent?: boolean
    disabled?: boolean
}

const riskListColumns: ColumnsType<RecordType> = [
    {
        dataIndex: 'groupCode',
        title: '规则分组'
    },
    {
        dataIndex: 'ruleName',
        title: '规则名称'
    },
    {
        dataIndex: 'riskLevelName',
        title: '风险等级'
    },
    {
        dataIndex: 'riskLevelDesc',
        title: '描述'
    },
    {
        dataIndex: 'disposeMethodName',
        title: '建议处置方式'
    },
    {
        dataIndex: 'replyScript',
        title: '客服话术'
    }
]
const ActiveCardDetail: React.FC<IActiveCardDetailProps> = props => {
    const { activeCardInfo = {} as RecordType, disabled } = props;
    const [form] = Form.useForm();
    const { riskList, historyFlowIds = [] } = activeCardInfo;
    const [operation, setOperation] = useState<{ [key: string]: 0 | 1 | undefined }>({});
    const [loading, setLoading] = useState(false);
    const hasRiskList = activeCardInfo.riskType === 'JD_RISK';

    console.debug('activeCardInfo',activeCardInfo)
    const isFinished = useMemo(() => activeCardInfo.status === 1 || disabled, [activeCardInfo.status, disabled]);

    const handleOperationChange = (e: any, record: Record<string, any>) => {
        setOperation(prev => ({ ...prev, [record._id]: e.target.value }))
    }

    const handleAllOperationChange = (e: any) => {
        console.log(e.target.checked)
        const { checked } = e.target;
        if (checked) {
            setOperation(riskList.reduce((acc: { [key: string]: 0 | 1 | undefined }, curr: Record<string, any>) => {
                acc[curr._id] = 0;
                return acc;
            }, {}))
        }
    }

    const onFinish = (values: any) => {
        console.log(values)
        if (hasRiskList && riskList?.length !== Object.keys(operation).length) {
            message.error('请选择所有风险点')
            return;
        }
        setLoading(true);
        updateRisk(Object.assign({
            flowId: props.flowId,
            riskFlagId: activeCardInfo.id,
            ...values,
        }, hasRiskList ? {
            riskList: riskList?.map((item: Record<string, any>) => ({ _id: item._id, feedback: operation[item._id] }))
        } : {})).then(res => {
            if (res.errCode === 0) {
                message.success('操作成功')
                props.onEvent('refreshRiskList')
                // 刷新当前组件状态
            } else {
                message.error(res.errMsg)
            }
        }).catch(err => {
            console.log(err)
        }).finally(() => {
            setLoading(false);
        })
    }

    const columns: ColumnsType<RiskListItemType> = useMemo(() => {
        if (!hasRiskList) {
            return [];
        }
        return [
            ...riskListColumns,
            {
                dataIndex: 'feedback',
                title: <>操作反馈 {!isFinished ? (
                        <Checkbox checked={riskList.every(item => operation[item._id] === 0)} onChange={handleAllOperationChange} style={{ marginLeft: 10 }}>全选通过</Checkbox>
                    ) : null}
                    </>,
                render: (feedback: number | undefined, record: Record<string, any>) => {
                    return !isFinished ? (
                        <Radio.Group onChange={(e) => handleOperationChange(e, record)} value={operation[record._id]}>
                            <Radio value={0}>通过</Radio>
                            <Radio value={1}>拦截</Radio>
                        </Radio.Group>
                    ) : (
                        <span>{feedback === 0 ? '通过' : '拦截'}</span>
                    )
                }
            }]
    }, [hasRiskList, riskList, operation, isFinished])


    useEffect(() => {
        form.setFieldsValue({
            status: activeCardInfo.status === 1 ? 1 : undefined,
            doResult: activeCardInfo.doResult,
            reason: activeCardInfo.reason,
        })
    }, [activeCardInfo])


    return (
        <div className="activeDetailWrapper">
            <h3 className='activeDetailWrapper-title'>风险详情</h3>
            <Card title="风险值" size='small'>
                <div className='riskBasicInfo'>
                    <p>基础维度：{activeCardInfo.rule?.dimensions}</p>
                    <p>满足条件：{activeCardInfo.rule?.conditions}</p>
                    <p>触发时间：{activeCardInfo.createTime ? dayjs(activeCardInfo.createTime).format('YYYY-MM-DD HH:mm:ss') : ''}</p>
                </div>
            </Card>
            {
                historyFlowIds?.length > 0 ? (<Card title="历史出险" size='small'>
                    <div className='historyFlowInfo'>
                        {
                            historyFlowIds?.map((flowId: string) => {
                                return (
                                    <a key={flowId} onClick={() => props.onEvent('toFlowDetail', { flowId })}>{flowId}</a>
                                )
                            })
                        }
                    </div>
                </Card>) : null
            }
            {
                hasRiskList ? (<>
                    <Card title="风险点" size='small'>
                        <Table dataSource={riskList} columns={columns} pagination={false} rowKey="_id" />
                    </Card>
                </>) : null
            }
            <Card title="处理风险" size='small' extra={activeCardInfo.doTime ? <span style={{ color: '#B4B4B4' }}>处理时间：{dayjs(activeCardInfo.doTime).format('YYYY-MM-DD HH:mm:ss')}</span> : null}>
                <Form
                    form={form}
                    layout='vertical'
                    name="basic"
                    onFinish={onFinish}
                    autoComplete="off"
                    initialValues={{
                        status: activeCardInfo.status,
                        doResult: activeCardInfo.doResult,
                        reason: activeCardInfo.reason,
                    }}
                >
                    <Row>
                        <Col span={5}>
                            <Form.Item
                                label="状态"
                                name="status"
                                rules={[{ required: true, message: '请选择状态' }]}
                            >
                                <Select options={[{ label: '已处理', value: 1 }]} placeholder='请选择状态' disabled={isFinished} />
                            </Form.Item>
                        </Col>
                        <Col span={1}></Col>
                        <Col span={5}>

                            <Form.Item
                                label="结果"
                                name="doResult"
                                rules={[{ required: true, message: '请选择结果' }]}
                            >
                                <Select
                                    placeholder='请选择结果'
                                    options={[{ label: '通过', value: 1 }, { label: '拦截', value: 0 }]}
                                    disabled={isFinished}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Form.Item label="详细描述" name="reason">
                                <Input.TextArea rows={4} placeholder='请输入' disabled={isFinished} />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={loading} disabled={isFinished}>
                                    提交
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
            
        </div>
    )
}

export default ActiveCardDetail;